import * as React from 'react'
import { TaxGroup, API } from '@getgreenline/homi-shared'
import { ProductModels } from '@getgreenline/products'
import { IExtraFieldOption, ProductStatusType } from './'
import { IExtraFields } from '../../../../utilities/LocalStorage'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { ProductStore } from '../../../../stores/CurrentCompanyStore/ProductStore'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { message } from 'antd'
import { ProductFilter } from './ProductFilter'
import { CreateProduct } from '../AddProduct/ProductStore'
import { Track } from '../../../../Track'
import { ProductListTable } from './ProductListTable'
import { DeleteWarningModal } from '../AddProduct/DeleteWarningModal'
import { ConvertVariantModal } from '../AddProduct/VariantsSection/ConvertVariantModal'
import { MergeProductModal } from '../AddProduct/VariantsSection/MergeProductModal'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  productFilter: ProductFilter
  currentUserStore?: CurrentUserStore
  products: ProductModels.INestedProduct[]
  extraFields: IExtraFields
  extraFieldOptions: IExtraFieldOption[]
  duplicatingProduct?: ProductModels.INestedProduct
  canEditProducts: boolean
  taxGroups: TaxGroup[]
  onChange: () => void
  setDuplicatingProduct: (product?: ProductModels.INestedProduct) => void
  defaultExpanded: boolean
  productStatus: ProductStatusType
}

interface State {
  expandedProductIds: string[]
  selectedProduct?: ProductModels.INestedProduct
  showConvertMode: 'standalone' | 'variant' | undefined
  showMergeModal: boolean
  showDeleteModal: boolean
}

export type ToggleModalType = 'standalone' | 'variant' | 'merge' | 'delete'

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class ProductList extends React.Component<Props, State> {
  productStore: ProductStore

  constructor(props: Props) {
    super(props)

    this.productStore = this.props.currentCompanyStore!.productStore!
    this.state = {
      expandedProductIds: [],
      selectedProduct: undefined,
      showConvertMode: undefined,
      showMergeModal: false,
      showDeleteModal: false,
    }
  }

  toggleChildProducts = (product: ProductModels.INestedProduct) => {
    const { expandedProductIds } = this.state
    const foundProductIdIndex = expandedProductIds.indexOf(product.id)

    if (foundProductIdIndex >= 0) {
      // Exists. Remove.
      this.setState({
        expandedProductIds: expandedProductIds.filter((productId) => productId !== product.id),
      })
    } else {
      // Doesn't exist. Add.
      const newExpandedProductIds = expandedProductIds
      newExpandedProductIds.push(product.id)
      this.setState({
        expandedProductIds: newExpandedProductIds,
      })
    }
  }

  duplicate = async (product: ProductModels.INestedProduct) => {
    const company = this.props.currentCompanyStore!.company!
    const { id, ...nestedProduct } = JSON.parse(
      JSON.stringify(product),
    ) as ProductModels.INestedProduct

    nestedProduct.sku = null
    nestedProduct.childProducts.forEach((childProduct) => {
      childProduct.sku = null
    })
    const createProduct = new CreateProduct()

    this.props.setDuplicatingProduct(product)
    createProduct.populateFields(nestedProduct)
    createProduct.setName(createProduct.name + ' (duplicate)')
    createProduct.setSKU(null)
    createProduct.setWooCommerceProductId(null)

    try {
      const createdProduct = await this.productStore.addProduct(
        company.id,
        createProduct.networkObject,
      )

      // Product duplicated. Loading products in the background
      Track.track('dashboard:products_duplicated', createdProduct)
      message.success(`${createdProduct.name} successfully added`)
      this.props.onChange()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error duplicating your product')
    } finally {
      this.props.setDuplicatingProduct(undefined)
    }
  }

  delete = async () => {
    if (!this.state.selectedProduct) return

    const company = this.props.currentCompanyStore!.company!
    if (window.confirm('Are you sure you want to delete this product?')) {
      Track.track('dashboard:products_deleted', this.state.selectedProduct)

      try {
        const deletedProduct = await this.productStore.deleteProduct(
          company.id,
          this.state.selectedProduct.id,
        )
        message.success(`${deletedProduct.name} successfully deleted`)
        this.props.onChange()
      } catch (error) {
        message.error(parseErrorMsg(error) || 'There was an error deleting your product')
      } finally {
        this.setState({ selectedProduct: undefined })
      }
    }
  }

  undelete = async (product: ProductModels.INestedProduct) => {
    const company = this.props.currentCompanyStore!.company!
    const undeleteProduct = new CreateProduct()

    const productId = product.parentProductId ? product.parentProductId : product.id
    try {
      const originalProduct = await API.getProductById(company.id, productId, true)
      undeleteProduct.populateFields(originalProduct)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error finding your product')
    }

    const updateObject = undeleteProduct.undelete(product)

    try {
      const undeletedProduct = await this.productStore.editProduct(
        company.id,
        productId,
        updateObject,
      )
      message.success(`${undeletedProduct.name} successfully undeleted`)
      this.props.onChange()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error undeleting your product')
    }
  }

  toggleModal = (type: ToggleModalType, product: ProductModels.INestedProduct) => {
    switch (type) {
      case 'standalone':
        return this.setState({ showConvertMode: 'standalone', selectedProduct: product })
      case 'variant':
        return this.setState({ showConvertMode: 'variant', selectedProduct: product })
      case 'merge':
        return this.setState({ showMergeModal: true, selectedProduct: product })
      case 'delete':
        return this.setState({ showDeleteModal: true, selectedProduct: product })
    }
  }

  render() {
    const { products, extraFields, duplicatingProduct, canEditProducts, defaultExpanded } =
      this.props
    const { selectedProduct, expandedProductIds } = this.state

    const productRowsToShow: ProductModels.INestedProduct[] = []
    products.forEach((product) => {
      productRowsToShow.push(product)
      const childProducts = product.childProducts
      if (childProducts.length > 0) {
        childProducts.forEach((childProduct) => {
          if (defaultExpanded) {
            if (
              !(
                childProduct.parentProductId &&
                expandedProductIds.includes(childProduct.parentProductId)
              )
            ) {
              const object = Object.assign(childProduct, { childProducts: [] })
              productRowsToShow.push(object)
            }
          } else if (
            childProduct.parentProductId &&
            expandedProductIds.includes(childProduct.parentProductId)
          ) {
            const object = Object.assign(childProduct, { childProducts: [] })
            productRowsToShow.push(object)
          }
        })
      }
    })

    return (
      <div
        className='table-responsive mb-4 white-rounded-container'
        style={{ minHeight: 300, padding: 0 }}
      >
        <ProductListTable
          products={productRowsToShow}
          productFilter={this.props.productFilter}
          canEdit={canEditProducts}
          toggleChildProducts={this.toggleChildProducts}
          duplicate={this.duplicate}
          isDuplicating={
            duplicatingProduct && this.state.selectedProduct?.id === duplicatingProduct.id
          }
          toggleModal={this.toggleModal}
          extraFields={extraFields}
          showDeleteModal={this.state.showDeleteModal}
          productStatus={this.props.productStatus}
          undelete={this.undelete}
        />

        {this.state.showConvertMode && selectedProduct && (
          <ConvertVariantModal
            mode={this.state.showConvertMode}
            parentName={selectedProduct.parentProductName || ''}
            productId={selectedProduct.id}
            productName={selectedProduct.name}
            onHide={() => this.setState({ showConvertMode: undefined, selectedProduct: undefined })}
            onSubmit={this.props.onChange}
          />
        )}

        {this.state.showMergeModal && selectedProduct && (
          <MergeProductModal
            productId={selectedProduct.id}
            isParentBatchTracked={selectedProduct.isBatchTracked}
            onClose={() => this.setState({ showMergeModal: false, selectedProduct: undefined })}
            onSubmit={this.props.onChange}
          />
        )}

        {this.state.showDeleteModal && selectedProduct && (
          <DeleteWarningModal
            product={selectedProduct}
            productName={
              selectedProduct.parentProductName
                ? `${selectedProduct.parentProductName} - ${selectedProduct.name}`
                : selectedProduct.name
            }
            isVariant={!!selectedProduct.parentProductId}
            hasVariants={!!selectedProduct.isMasterProduct}
            submit={this.delete}
            isLoading={false}
            isVisible={this.state.showDeleteModal}
            onClose={() => this.setState({ showDeleteModal: false, selectedProduct: undefined })}
          />
        )}
      </div>
    )
  }
}
